import { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { SEO } from '../components/SEO';
import { ImageIcon, Search, Grid, List } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Images = () => {
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [imagesRes, categoriesRes] = await Promise.all([
          fetch(`${API_URL}/api/images`),
          fetch(`${API_URL}/api/images/categories`)
        ]);
        
        if (imagesRes.ok) {
          const data = await imagesRes.json();
          setImages(data);
        }
        if (categoriesRes.ok) {
          const cats = await categoriesRes.json();
          setCategories(cats);
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to fetch images:', err);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredImages = images.filter(img => {
    const matchesSearch = !searchTerm || 
      img.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (img.altText && img.altText.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = !filterCategory || img.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <Layout>
      <SEO 
        title="Image Gallery"
        description="Browse photos from Geneva United Methodist Church - worship services, community events, and church family moments."
        url="/images"
      />
      
      {/* Hero Section */}
      <section className="relative bg-primary-900 text-white py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-primary-900/90" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-secondary font-medium mb-4">Media</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4">
            Image Gallery
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Browse photos from our church community
          </p>
        </div>
      </section>

      {/* Images Section */}
      <section className="py-16 bg-warm-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search images..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                />
              </div>
              <div className="flex items-center gap-3">
                {categories.length > 0 && (
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-secondary"
                  >
                    <option value="">All Categories</option>
                    {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                )}
                <div className="flex border border-slate-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 ${viewMode === 'grid' ? 'bg-secondary text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >
                    <Grid className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 ${viewMode === 'list' ? 'bg-secondary text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >
                    <List className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Images */}
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-4 border-secondary border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="mt-4 text-slate-500">Loading images...</p>
            </div>
          ) : filteredImages.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
              <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading text-lg font-semibold text-slate-600 mb-2">
                {searchTerm || filterCategory ? 'No images found' : 'No images available'}
              </h3>
              <p className="text-slate-500">
                {searchTerm || filterCategory ? 'Try adjusting your filters.' : 'Check back later for photos.'}
              </p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredImages.map((image) => (
                <div
                  key={image.id}
                  className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => setSelectedImage(image)}
                >
                  <div className="aspect-square">
                    <img
                      src={`${API_URL}${image.imageUrl}`}
                      alt={image.altText || image.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-3">
                    <p className="font-medium text-sm text-primary-900 truncate">{image.title}</p>
                    <p className="text-xs text-slate-500">{image.category}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredImages.map((image) => (
                <div
                  key={image.id}
                  className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => setSelectedImage(image)}
                >
                  <img
                    src={`${API_URL}${image.imageUrl}`}
                    alt={image.altText || image.title}
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                  <div>
                    <p className="font-medium text-primary-900">{image.title}</p>
                    <p className="text-sm text-slate-500">{image.category}</p>
                    {image.altText && <p className="text-xs text-slate-400 mt-1">{image.altText}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Image Count */}
          {!loading && filteredImages.length > 0 && (
            <p className="text-center text-slate-500 mt-8">
              Showing {filteredImages.length} image{filteredImages.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="max-w-5xl max-h-[90vh] relative">
            <img
              src={`${API_URL}${selectedImage.imageUrl}`}
              alt={selectedImage.altText || selectedImage.title}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            <div className="text-center mt-4">
              <p className="text-white font-heading text-lg">{selectedImage.title}</p>
              {selectedImage.altText && (
                <p className="text-slate-400 text-sm">{selectedImage.altText}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Images;
